import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSharedWalletAnalytics } from '../../../hooks/useAnalytics';
import MetricCard from '../core/MetricCard';
import KPICard from '../core/KPICard';
import AreaChart from '../../ui/AreaChart';
import PieChart from '../../ui/PieChart';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { getPreviousPeriodValue } from '../../../utils/chartHelpers';
import { formatPercent, safeFormatFixed } from '../../../utils/format';
import { useFormatting } from '../../../hooks/useFormatting';
import { FILTER_PERIODS } from '../../../constants';
import './SharedWalletMetrics.css';

/**
 * Shared Wallet Metrics Component
 * Displays shared wallet-related analytics with thin, clean design
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const SharedWalletMetrics = ({ period, compact = false }) => {
  const { data: walletData, loading, error } = useSharedWalletAnalytics(period);

  // Generate chart data for key metrics
  const [totalWalletsChart, setTotalWalletsChart] = useState(null);
  const [balanceChart, setBalanceChart] = useState(null);
  const [previousWalletData, setPreviousWalletData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getSharedWalletAnalytics } = await import('../../../services/firebaseService');
        const prevWallet = await getSharedWalletAnalytics(prevPeriod).catch(() => null);
        setPreviousWalletData(prevWallet);
      } catch (error) {
        setPreviousWalletData(null);
      }
    };

    if (walletData) {
      fetchPreviousPeriodData();
    }
  }, [period, walletData]);

  useEffect(() => {
    if (!walletData) {
      setTotalWalletsChart(null);
      setBalanceChart(null);
      return;
    }

    const currentWallets = walletData.totalWallets || 0;
    const previousWallets = getPreviousPeriodValue(walletData, previousWalletData, 'totalWallets', 0.9);
    
    const currentBalance = walletData.totalBalance || 0;
    const previousBalance = getPreviousPeriodValue(walletData, previousWalletData, 'totalBalance', 0.9);
    
    Promise.all([
      transformToChartDataAsync(
        { totalWallets: currentWallets },
        { totalWallets: previousWallets },
        'totalWallets',
        period,
        {
          collectionName: 'sharedWallets',
          valueExtractor: () => 1
        }
      ),
      transformToChartDataAsync(
        { totalBalance: currentBalance },
        { totalBalance: previousBalance },
        'totalBalance',
        period,
        {
          collectionName: 'sharedWallets',
          valueExtractor: (docData) => parseFloat(docData.totalBalance || 0)
        }
      )
    ]).then(([walletsChart, balanceChartData]) => {
      setTotalWalletsChart(walletsChart);
      setBalanceChart(balanceChartData);
    }).catch(() => {
      setTotalWalletsChart(transformToChartData(
        { totalWallets: currentWallets },
        { totalWallets: previousWallets },
        'totalWallets',
        period
      ));
      setBalanceChart(transformToChartData(
        { totalBalance: currentBalance },
        { totalBalance: previousBalance },
        'totalBalance',
        period
      ));
    });
  }, [walletData, previousWalletData, period]);

  // Get formatting functions from hook
  const { formatCurrency, formatNumber } = useFormatting();

  // Normalize data for safe access - ensure all values are numbers
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const normalized = {
    totalWallets: safeNumber(walletData?.totalWallets),
    newWallets: safeNumber(walletData?.newWallets),
    totalBalance: safeNumber(walletData?.totalBalance),
    averageBalance: safeNumber(walletData?.averageBalance),
    totalFundingVolume: safeNumber(walletData?.totalFundingVolume),
    totalWithdrawalVolume: safeNumber(walletData?.totalWithdrawalVolume),
    fundingToWithdrawalRatio: safeNumber(walletData?.fundingToWithdrawalRatio),
    activeWallets: safeNumber(walletData?.activeWallets),
    walletsWithCards: safeNumber(walletData?.walletsWithCards),
    totalMembers: safeNumber(walletData?.totalMembers),
    averageMembersPerWallet: safeNumber(walletData?.averageMembersPerWallet),
    fundingCount: safeNumber(walletData?.fundingCount),
    withdrawalCount: safeNumber(walletData?.withdrawalCount)
  };

  // Calculate percentages
  const activeWalletPercentage = normalized.totalWallets > 0 
    ? (normalized.activeWallets / normalized.totalWallets) * 100 
    : 0;
  const cardLinkingRate = normalized.totalWallets > 0 
    ? (normalized.walletsWithCards / normalized.totalWallets) * 100 
    : 0;
  const netFlow = normalized.totalFundingVolume - normalized.totalWithdrawalVolume;

  // Memoize chart data
  const transactionTypeChartData = useMemo(() => {
    const funding = normalized.fundingCount || 0;
    const withdrawal = normalized.withdrawalCount || 0;
    if (funding === 0 && withdrawal === 0) return null;
    return [
      { label: 'Funding', value: funding },
      { label: 'Withdrawal', value: withdrawal }
    ].filter(item => item.value > 0);
  }, [normalized.fundingCount, normalized.withdrawalCount]);

  // Check if we have any data
  const hasData = normalized.totalWallets > 0 || normalized.totalBalance > 0 || normalized.totalMembers > 0;

  // Error state
  if (error) {
    return (
      <div className="shared-wallet-metrics-enhanced error">
        <h3>Error loading shared wallet metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  // No data state
  if (!hasData && !loading) {
    return (
      <div className="shared-wallet-metrics-enhanced">
        <section className="shared-wallet-hero">
          <div className="shared-wallet-hero-header">
            <h1 className="shared-wallet-title">
              <i className="bi bi-wallet2 icon-brand"></i>
              Shared Wallet Metrics
            </h1>
            <p className="shared-wallet-subtitle">{period} period</p>
          </div>
        </section>
        <div className="no-data-message">
          <p>No shared wallet data available for the selected period.</p>
          <p className="no-data-hint">Shared wallets will appear here once users create them.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-wallet-metrics-enhanced">
      {/* Hero Section - Key KPIs */}
      <section className="shared-wallet-hero">
        <div className="shared-wallet-hero-header">
          <h1 className="shared-wallet-title">
            <i className="bi bi-wallet2 icon-brand"></i>
            Shared Wallet Metrics
          </h1>
          <p className="shared-wallet-subtitle">{period} period</p>
        </div>
        
        <div className="shared-wallet-kpi-grid">
          <KPICard
                title="Total Wallets"
                value={normalized.totalWallets}
                formatType="number"
            icon="bi-wallet2"
                color="blue"
                subtitle={`${period} period`}
            loading={loading}
              />
          <KPICard
                title="Total Balance"
                value={normalized.totalBalance}
                formatType="currency"
            icon="bi-cash-coin"
                color="green"
                subtitle="across all wallets"
            loading={loading}
              />
          <KPICard
                title="Avg Balance"
                value={normalized.averageBalance}
                formatType="currency"
            icon="bi-bar-chart"
                color="purple"
                subtitle="per wallet"
            loading={loading}
              />
          <KPICard
                title="Active Wallets"
                value={normalized.activeWallets}
                formatType="number"
            icon="bi-check-circle"
                color="teal"
                subtitle={formatPercent(activeWalletPercentage, 1)}
            loading={loading}
              />
        </div>
      </section>

      {/* Wallet Activity Overview */}
      <section className="shared-wallet-section activity-section">
        <div className="section-header">
          <h2 className="section-title">
            <i className="bi bi-diagram-3 icon-brand"></i>
            Wallet Activity Overview
          </h2>
        </div>

        <div className="wallet-activity-grid">
          <div className="activity-card funding-card">
            <div className="activity-card-header">
              <i className="bi bi-arrow-down-circle activity-icon"></i>
              <h3>Funding</h3>
            </div>
            <div className="activity-card-content">
              <div className="activity-stat">
                <span className="activity-stat-label">Total Volume</span>
                <span className="activity-stat-value currency">{formatCurrency(normalized.totalFundingVolume)}</span>
              </div>
              <div className="activity-stat-mini">
                <span className="activity-stat-mini-label">Transactions</span>
                <span className="activity-stat-mini-value">{formatNumber(normalized.fundingCount)}</span>
              </div>
            </div>
          </div>

          <div className="activity-card withdrawal-card">
            <div className="activity-card-header">
              <i className="bi bi-arrow-up-circle activity-icon"></i>
              <h3>Withdrawals</h3>
            </div>
            <div className="activity-card-content">
              <div className="activity-stat">
                <span className="activity-stat-label">Total Volume</span>
                <span className="activity-stat-value currency">{formatCurrency(normalized.totalWithdrawalVolume)}</span>
              </div>
              <div className="activity-stat-mini">
                <span className="activity-stat-mini-label">Transactions</span>
                <span className="activity-stat-mini-value">{formatNumber(normalized.withdrawalCount)}</span>
              </div>
            </div>
          </div>

          <div className="activity-card netflow-card">
            <div className="activity-card-header">
              <i className="bi bi-arrow-left-right activity-icon"></i>
              <h3>Net Flow</h3>
            </div>
            <div className="activity-card-content">
              <div className="activity-stat">
                <span className="activity-stat-label">Net Balance</span>
                <span className={`activity-stat-value currency ${netFlow >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(netFlow)}
                </span>
              </div>
              <div className="activity-stat-mini">
                <span className="activity-stat-mini-label">Ratio</span>
                <span className="activity-stat-mini-value">{safeFormatFixed(normalized.fundingToWithdrawalRatio, 2)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Charts Section */}
      {(totalWalletsChart || balanceChart || transactionTypeChartData) && (
        <section className="shared-wallet-section charts-section">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-graph-up icon-brand"></i>
              Shared Wallet Trends
            </h2>
      </div>

          <div className="charts-grid">
            {totalWalletsChart && (
              <div className="chart-card">
                <h3 className="chart-title">Total Wallets Over Time</h3>
                <AreaChart
                  data={totalWalletsChart.data}
                  comparisonData={totalWalletsChart.comparisonData}
                  label="Total Wallets"
                  formatType="number"
                  color="blue"
                />
              </div>
            )}
            
            {balanceChart && (
              <div className="chart-card">
                <h3 className="chart-title">Total Balance Over Time</h3>
                <AreaChart
                  data={balanceChart.data}
                  comparisonData={balanceChart.comparisonData}
                  label="Total Balance"
                  formatType="currency"
                  color="green"
                />
              </div>
            )}
            
      {transactionTypeChartData && (
              <div className="chart-card">
                <h3 className="chart-title">Transaction Type Distribution</h3>
          <PieChart
            data={transactionTypeChartData}
                  label="Transaction Types"
            formatType="number"
            showPercentages={true}
            innerRadius={0.5}
          />
        </div>
      )}
          </div>
        </section>
      )}

      {/* Additional Metrics - Collapsible */}
      <CollapsibleSection title="Additional Shared Wallet Metrics" defaultExpanded={false}>
        <div className="metrics-grid-wrapper">
          <div className="metrics-grid">
            {/* Wallet Activity */}
            <div className="metric-group">
            <h3 className="group-title">Wallet Activity</h3>
            <div className="metric-cards">
              <MetricCard
                title="New Wallets"
                value={normalized.newWallets}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-plus-circle icon-brand"></i>}
                color="green"
                subtitle={`${period} period`}
              />
              <MetricCard
                title="Wallets with Cards"
                value={normalized.walletsWithCards}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-credit-card icon-brand"></i>}
                color="indigo"
                subtitle={formatPercent(cardLinkingRate, 1)}
              />
            </div>
            </div>

            {/* Member Metrics */}
            <div className="metric-group">
            <h3 className="group-title">Member Metrics</h3>
            <div className="metric-cards">
              <MetricCard
                title="Total Members"
                value={normalized.totalMembers}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-people icon-brand"></i>}
                color="indigo"
                subtitle="across all wallets"
              />
              <MetricCard
                title="Avg Members"
                value={safeFormatFixed(normalized.averageMembersPerWallet, 1)}
                loading={loading}
                icon={<i className="bi bi-bar-chart icon-brand"></i>}
                color="purple"
                subtitle="per wallet"
              />
            </div>
            </div>
          </div>
            </div>
      </CollapsibleSection>
    </div>
  );
};

SharedWalletMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

SharedWalletMetrics.defaultProps = {
  compact: false
};

export default React.memo(SharedWalletMetrics);
